import { CreateRateItemRequest, RateItem } from '../models/rate.types';

export interface DuplicateCheckResult {
  hasDuplicate: boolean;
  hasZoneConflict: boolean;
  conflictingItemTitle?: string;
}

interface ShippingKey {
  courier_id: number | null | undefined;
  courier_service_id: number | null | undefined;
  country_id: number | null | undefined;
  courier_zone_id: number | null | undefined;
  title: string;
}

function extractShippingKey(
  item: RateItem | CreateRateItemRequest
): ShippingKey | null {
  if (item.type !== 'shipping') return null;
  return {
    courier_id: item.courier_id,
    courier_service_id: item.courier_service_id,
    country_id: item.country_id,
    courier_zone_id: item.courier_zone_id,
    title: item.title,
  };
}

const normalizeNull = (val: number | null | undefined): number | null =>
  val == null ? null : val;

export function checkShippingDuplicate(
  candidate: CreateRateItemRequest,
  existingItems: (RateItem | CreateRateItemRequest)[]
): DuplicateCheckResult {
  if (candidate.type !== 'shipping') {
    return { hasDuplicate: false, hasZoneConflict: false };
  }

  const candidateCourier = normalizeNull(candidate.courier_id);
  const candidateService = normalizeNull(candidate.courier_service_id);
  const candidateCountry = normalizeNull(candidate.country_id);
  const candidateZone = normalizeNull(candidate.courier_zone_id);

  for (const item of existingItems) {
    const key = extractShippingKey(item);
    if (!key) continue;

    const itemCourier = normalizeNull(key.courier_id);
    const itemService = normalizeNull(key.courier_service_id);
    const itemCountry = normalizeNull(key.country_id);
    const itemZone = normalizeNull(key.courier_zone_id);

    const sameBase =
      candidateCourier === itemCourier &&
      candidateService === itemService &&
      candidateCountry === itemCountry;

    if (!sameBase) continue;

    if (candidateZone === itemZone) {
      return {
        hasDuplicate: true,
        hasZoneConflict: false,
        conflictingItemTitle: key.title,
      };
    }

    const oneHasZone = (candidateZone === null) !== (itemZone === null);
    if (oneHasZone) {
      return {
        hasDuplicate: false,
        hasZoneConflict: true,
        conflictingItemTitle: key.title,
      };
    }
  }

  return { hasDuplicate: false, hasZoneConflict: false };
}

export function findDuplicateShippingPair(
  items: CreateRateItemRequest[]
): { index1: number; index2: number } | null {
  const shippingItems = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.type === 'shipping');

  for (let i = 0; i < shippingItems.length; i++) {
    for (let j = i + 1; j < shippingItems.length; j++) {
      const result = checkShippingDuplicate(shippingItems[i].item, [
        shippingItems[j].item,
      ]);
      if (result.hasDuplicate) {
        return {
          index1: shippingItems[i].index,
          index2: shippingItems[j].index,
        };
      }
    }
  }

  return null;
}
